import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { applyIamAuthHeaders } from './applyIamAuthHeaders'
import { getApiBaseUrl } from '@/config/apiBaseUrl'
import { API_TIMEOUT_MS } from '@/api/timeouts'


/** Prefer SAP/CPI business messages from API error bodies. */
function extractApiErrorMessage(data: unknown, fallback: string): string {
  if (data == null) return fallback
  if (typeof data === 'string' && data.trim()) return data.trim()
  if (typeof data !== 'object') return fallback
  const d = data as Record<string, unknown>
  if (typeof d.message === 'string' && d.message.trim()) return d.message.trim()
  if (typeof d.error === 'string' && d.error.trim() && d.error !== 'sap_business_error') {
    return d.error.trim()
  }
  const sap = d.sap
  if (sap && typeof sap === 'object') {
    const root = sap as Record<string, unknown>
    const resp = root.Response as Record<string, unknown> | undefined
    const item = resp?.item
    if (item && typeof item === 'object' && !Array.isArray(item)) {
      const msg = (item as Record<string, unknown>).Message ?? (item as Record<string, unknown>).MESSAGE
      if (msg != null && String(msg).trim()) return String(msg).trim()
    }
    if (Array.isArray(item)) {
      const msgs = item
        .map((row) => {
          if (!row || typeof row !== 'object') return ''
          const m = (row as Record<string, unknown>).Message ?? (row as Record<string, unknown>).MESSAGE
          return m != null ? String(m).trim() : ''
        })
        .filter(Boolean)
      if (msgs.length) return msgs.join(', ')
    }
    const rd = root.Response_Data as Record<string, unknown> | undefined
    if (rd && typeof rd.Message === 'string' && rd.Message.trim()) return rd.Message.trim()
  }
  return fallback
}

const rfidClient: AxiosInstance = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: API_TIMEOUT_MS,
  headers: { 'Content-Type': 'application/json', accept: 'application/json' },
  withCredentials: true,
})

rfidClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  config.baseURL = getApiBaseUrl()
  applyIamAuthHeaders(config)
  return config
})

rfidClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const data = error.response?.data
    const statusCode = error.response?.status
    const raw = extractApiErrorMessage(data, error.message || 'RFID request failed')

    return Promise.reject({
      message: raw,
      statusCode,
    })
  }
)

export default rfidClient
